import { BaseElement } from 'base';
import { cubicBezier } from 'cubic-bezier';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const SCROLL_MS = 300;
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
const ease = cubicBezier(0.2, 0, 0, 1);

const revealDelta = (branch, zone) => {
  if (branch.top >= zone.top && branch.bottom <= zone.bottom) return 0;
  if (branch.height > zone.height) return branch.top - zone.top;
  if (branch.bottom > zone.bottom) return branch.bottom - zone.bottom;
  return branch.top - zone.top;
};

class MenuScroll extends BaseElement {
  #frame = 0;
  #revealing = null;
  #hidden = new Map();
  #reducedMotionQuery = null;

  onConnect() {
    this.#reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    this.listen(this, 'toggle', this.#onToggle, true);
    USER_SCROLL_EVENTS.forEach(type =>
      this.listen(this, type, this.#cancel, { passive: true })
    );
    const observer = new ResizeObserver(this.#onResize.bind(this));
    [this, ...this.qa('[data-menu-scroll-section]')].forEach(target =>
      this.observe(observer, target)
    );
    this.cleanup(() => this.#cancel());
  }

  #onToggle(event) {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;
    if (!details.open) {
      if (this.#revealing && details.contains(this.#revealing)) this.#cancel();
      return;
    }

    const delta = revealDelta(
      details.getBoundingClientRect(),
      this.getBoundingClientRect()
    );
    if (delta !== 0) this.#scrollBy(delta, details);
  }

  #onResize(entries) {
    entries.forEach(({ target, contentRect }) => {
      const hidden = contentRect.width === 0 && contentRect.height === 0;
      if (hidden === (this.#hidden.get(target) ?? false)) return;
      this.#hidden.set(target, hidden);

      if (target === this) this.#onSelfResize(hidden);
      else if (hidden) this.#resetSection(target);
    });
  }

  #onSelfResize(hidden) {
    if (!hidden) {
      this.scrollTop = 0;
      return;
    }
    this.#cancel();
    this.qa('details[open]').forEach(details =>
      details.removeAttribute('open')
    );
  }

  #resetSection(section) {
    if (this.#revealing && section.contains(this.#revealing)) this.#cancel();
    this.qa('details[open]')
      .filter(details => section.contains(details))
      .forEach(details => details.removeAttribute('open'));
  }

  #scrollBy(delta, details) {
    this.#cancel();
    const from = this.scrollTop;

    if (this.#reducedMotionQuery.matches) {
      this.scrollTop = from + delta;
      return;
    }

    let start = null;
    const step = timestamp => {
      start ??= timestamp;
      const progress = Math.min((timestamp - start) / SCROLL_MS, 1);
      this.scrollTop = from + delta * ease(progress);
      if (progress < 1) this.#frame = requestAnimationFrame(step);
      else this.#finish();
    };
    this.#revealing = details;
    this.#frame = requestAnimationFrame(step);
  }

  #cancel() {
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#finish();
  }

  #finish() {
    this.#frame = 0;
    this.#revealing = null;
  }
}

if (!customElements.get('menu-scroll'))
  customElements.define('menu-scroll', MenuScroll);
